import { useTranslation } from "react-i18next";
import type { ApprovalWorkflow } from "@/pages/admin/approvals/hooks/useApprovals";

interface WorkflowPanelProps {
  workflows: ApprovalWorkflow[];
  loading: boolean;
  onCreate: () => void;
  onEdit: (workflow: ApprovalWorkflow) => void;
  onDelete: (workflow: ApprovalWorkflow) => void;
}

// 审批流配置面板：展示每条审批流的适用席位门槛、启停状态与逐级审批节点。
export default function WorkflowPanel({ workflows, loading, onCreate, onEdit, onDelete }: WorkflowPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-flow-chart" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("approvals.workflowsTitle")}</h2>
            <p className="text-[11px] text-foreground-500">{t("approvals.workflowsSub")}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-add-line" />
          </span>
          {t("approvals.newWorkflow")}
        </button>
      </div>

      {loading ? (
        <div className="mt-5 flex flex-col gap-2">
          {[0, 1].map((item) => (
            <div key={item} className="h-20 rounded-md bg-background-100" />
          ))}
        </div>
      ) : workflows.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-flow-chart text-lg" />
          </span>
          <p className="text-sm font-medium text-foreground-800">{t("approvals.workflowsEmpty")}</p>
          <p className="text-xs text-foreground-500">{t("approvals.workflowsEmptyDesc")}</p>
        </div>
      ) : (
        <ul className="mt-5 flex flex-col gap-3">
          {workflows.map((workflow) => (
            <li key={workflow.id} className="rounded-md border border-background-200 bg-background-100 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground-950">{workflow.name}</p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        workflow.enabled ? "bg-primary-100 text-primary-700" : "bg-background-200 text-foreground-500"
                      }`}
                    >
                      {workflow.enabled ? t("approvals.enabled") : t("approvals.disabled")}
                    </span>
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-900">
                      {workflow.min_seats > 0
                        ? t("approvals.minSeatsTag", { count: workflow.min_seats })
                        : t("approvals.allSeatsTag")}
                    </span>
                  </div>
                  {workflow.description && (
                    <p className="mt-1 text-xs text-foreground-600">{workflow.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onEdit(workflow)}
                    className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-edit-line" />
                    {t("approvals.edit")}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(workflow)}
                    className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                  >
                    <i className="ri-delete-bin-line" />
                    {t("approvals.delete")}
                  </button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {workflow.steps.map((step, index) => (
                  <span key={step.id} className="inline-flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-700">
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-secondary-100 text-[9px] font-semibold text-secondary-900">
                        {index + 1}
                      </span>
                      {step.name}
                      <span className="text-foreground-400">
                        {step.approver_role === "admin" ? t("approvals.roleAdmin") : t("approvals.roleStaff")}
                      </span>
                      <span
                        className="flex items-center gap-0.5 text-foreground-400"
                        title={t("approvals.stepSlaChip", { hours: step.sla_hours ?? 24 })}
                      >
                        <i className="ri-alarm-warning-line" />
                        {step.sla_hours ?? 24}h
                      </span>
                      {(step.escalate_after_hours ?? 0) > 0 && (
                        <span
                          className="flex items-center gap-0.5 text-secondary-700"
                          title={t("approvals.stepEscalateChip", { hours: step.escalate_after_hours })}
                        >
                          <i className="ri-arrow-up-double-line" />
                          {step.escalate_after_hours}h
                        </span>
                      )}
                    </span>
                    {index < workflow.steps.length - 1 && (
                      <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
                        <i className="ri-arrow-right-s-line" />
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}